import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Drawer from "@mui/material/Drawer";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import NotesIcon from "@mui/icons-material/Notes";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

import apiService from "../../service/Api"; // adjust path to match this file's real location
import { ArrowBack } from "@mui/icons-material";

// ---------------------------------------------------------------------------
// Types — mirror the backend serializers.
//
// NOTE: `venue_name` is not yet on CaseListItem/CaseDetail in the current
// serializers (they only carry stream_title). It's used below because the
// brief asks for venue in the table/detail. Add `venue_name` (e.g.
// stream.venue.name) to CostAnalysisListSerializer / DetailSerializer, or
// swap the two references below for whatever field you expose.
// ---------------------------------------------------------------------------

type StatusValue = "draft" | "awaiting_audit" | "approved" | "rejected";
type SubmitTargetValue = "finance" | "ceo" | "producer";

type CaseListItem = {
  id: number;
  case_number: string;
  title: string;
  stream_title: string;
  venue_name?: string | null;
  status: StatusValue;
  submitted_to: SubmitTargetValue | "";
  total_amount: string;
  submitted_at: string | null;
  created_at: string;
  decided_at?: string | null;
  decided_by_name?: string | null;
  rejection_reason?: string | null;
};

type CostLineItem = {
  id: number;
  label: string;
  amount: string;
  source: "auto" | "manual";
};

type ActivityEvent = {
  id: string;
  case_id: number;
  case_number: string;
  title: string;
  type: "submitted" | "approved" | "rejected";
  submitted_to?: SubmitTargetValue | "";
  time: string;
};
type CostCategoryItem = {
  id: number;
  category_type: string;
  category_type_display: string;
  subtitle: string;
  amount: string;
  line_items: CostLineItem[];
};

type CaseDetail = CaseListItem & {
  client_ref: string;
  notes: string;
  categories: CostCategoryItem[];
};

type Stats = {
  pending_count: number;
  pending_total: number;
  approved_this_month_count: number;
  approved_this_month_total: number;
  rejected_this_month_count: number;
  avg_approval_hours: number | null;
};

type MainTab = "pending" | "approved" | "rejected" | "all";

// ---------------------------------------------------------------------------
// Design tokens — kept consistent with the referral/partners dashboard:
// warm cream surfaces, a terracotta primary, and the same soft accent tints
// used for status.
// ---------------------------------------------------------------------------

const PAPER = "#FBF8F5";
const SURFACE = "#F7F2ED";
const BORDER = "#EAE1D8";
const INK = "#2E2822";
const SUBTLE = "#8A7C6E";

const STATUS_ACCENT: Record<StatusValue, string> = {
  draft: "#A79A8E",
  awaiting_audit: "#C7793D",
  approved: "#5C8A4C",
  rejected: "#B5544B",
};

const STATUS_LABEL: Record<StatusValue, string> = {
  draft: "Draft",
  awaiting_audit: "Awaiting Audit",
  approved: "Approved",
  rejected: "Rejected",
};

const STAT_ACCENTS = {
  pending: { bg: "#FBF0E6", badge: "#C7793D", glow: "#E9C29A" },
  approved: { bg: "#EEF4EA", badge: "#5C8A4C", glow: "#B9D6AA" },
  rejected: { bg: "#FAEDEC", badge: "#B5544B", glow: "#E6B3AE" },
  time: { bg: "#EDF1F4", badge: "#5A7B92", glow: "#B7CBD8" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatMoney = (value: string | number) =>
  `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const formatRelative = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
};

const isUrgent = (c: CaseListItem) => {
  if (c.status !== "awaiting_audit" || !c.submitted_at) return false;
  return (Date.now() - new Date(c.submitted_at).getTime()) / 36e5 > 24;
};

const outlinedBtnSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  color: INK,
  borderColor: BORDER,
  bgcolor: "#fff",
  borderRadius: 2,
  "&:hover": { borderColor: "#D8CFC7", bgcolor: "#fff" },
};

const cardSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: BORDER,
  bgcolor: "#fff",
};

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

function StatCard({
  accent,
  icon,
  label,
  value,
  sub,
}: {
  accent: { bg: string; badge: string; glow: string };
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        flex: 1,
        minWidth: 210,
        borderRadius: 3,
        border: "1px solid",
        borderColor: BORDER,
        bgcolor: accent.bg,
        p: 2.75,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -28,
          right: -28,
          width: 96,
          height: 96,
          borderRadius: "50%",
          bgcolor: accent.glow,
          opacity: 0.45,
          filter: "blur(6px)",
        }}
      />
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: accent.badge,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.75,
          position: "relative",
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.07em",
          color: SUBTLE,
          position: "relative",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 26,
          color: INK,
          mt: 0.25,
          position: "relative",
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          sx={{ color: SUBTLE, position: "relative" }}
        >
          {sub}
        </Typography>
      )}
    </Card>
  );
}

function StatsStrip({
  stats,
  loading,
}: {
  stats: Stats | null;
  loading: boolean;
}) {
  if (loading || !stats) {
    return (
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <Card
            key={i}
            elevation={0}
            sx={{
              ...cardSx,
              flex: 1,
              minWidth: 210,
              height: 118,
              bgcolor: SURFACE,
            }}
          />
        ))}
      </Stack>
    );
  }
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
      <StatCard
        accent={STAT_ACCENTS.pending}
        icon={<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />}
        label="AWAITING CLEARANCE"
        value={formatMoney(stats.pending_total)}
        sub={`${stats.pending_count} case${stats.pending_count === 1 ? "" : "s"}`}
      />
      <StatCard
        accent={STAT_ACCENTS.approved}
        icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
        label="APPROVED THIS MONTH"
        value={formatMoney(stats.approved_this_month_total)}
        sub={`${stats.approved_this_month_count} case${stats.approved_this_month_count === 1 ? "" : "s"}`}
      />
      <StatCard
        accent={STAT_ACCENTS.rejected}
        icon={<HighlightOffIcon sx={{ fontSize: 20 }} />}
        label="REJECTED THIS MONTH"
        value={String(stats.rejected_this_month_count)}
        sub="sent back for revision"
      />
      <StatCard
        accent={STAT_ACCENTS.time}
        icon={<ScheduleIcon sx={{ fontSize: 20 }} />}
        label="AVG TIME TO DECISION"
        value={
          stats.avg_approval_hours == null
            ? "—"
            : stats.avg_approval_hours < 24
              ? `${Math.round(stats.avg_approval_hours)}h`
              : `${(stats.avg_approval_hours / 24).toFixed(1)}d`
        }
        sub="submission to decision"
      />
    </Stack>
  );
}

function StatusPill({ status }: { status: StatusValue }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        alignItems: "center",
        px: 1.1,
        py: 0.35,
        borderRadius: 5,
        bgcolor: `${STATUS_ACCENT[status]}1A`,
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: STATUS_ACCENT[status],
        }}
      />
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: STATUS_ACCENT[status] }}
      >
        {STATUS_LABEL[status]}
      </Typography>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Pending Requests panel (mirrors the "Payout Requests" card)
// ---------------------------------------------------------------------------

function PendingRequestsPanel({
  cases,
  loading,
  onOpen,
  onViewAll,
}: {
  cases: CaseListItem[];
  loading: boolean;
  onOpen: (id: number) => void;
  onViewAll: () => void;
}) {
  const preview = cases.slice(0, 4);
  return (
    <Card elevation={0} sx={{ ...cardSx, p: 3, flex: 1, minWidth: 320 }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            Pending Requests
          </Typography>
          {cases.length > 0 && (
            <Chip
              label={cases.length}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: STAT_ACCENTS.pending.badge,
                color: "#fff",
              }}
            />
          )}
        </Stack>
        <Button
          size="small"
          onClick={onViewAll}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: STAT_ACCENTS.pending.badge,
          }}
        >
          View all
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={22} />
        </Box>
      ) : preview.length === 0 ? (
        <Typography variant="body2" sx={{ color: SUBTLE, py: 2 }}>
          Nothing is waiting on a decision right now.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {preview.map((c) => (
            <Stack
              key={c.id}
              direction="row"
              onClick={() => onOpen(c.id)}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.25,
                py: 1.1,
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": { bgcolor: SURFACE },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    bgcolor: `${STAT_ACCENTS.pending.badge}1F`,
                    color: STAT_ACCENTS.pending.badge,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {c.case_number.replace("CASE #", "").slice(0, 2)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 13.5 }} noWrap>
                      {c.title || c.stream_title}
                    </Typography>
                    {isUrgent(c) && (
                      <Chip
                        label="URGENT"
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          fontWeight: 700,
                          bgcolor: "#EDE6E0",
                          color: "#6B5D53",
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" sx={{ color: SUBTLE }}>
                    {c.case_number} · {formatRelative(c.submitted_at)}
                  </Typography>
                </Box>
              </Stack>
              <Typography
                sx={{ fontWeight: 700, fontSize: 13.5, flexShrink: 0, pl: 1 }}
              >
                {formatMoney(c.total_amount)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent Activity panel
// ---------------------------------------------------------------------------

function RecentActivityPanel({
  events,
  loading,
}: {
  events: ActivityEvent[];
  loading: boolean;
}) {
  const iconFor = (type: ActivityEvent["type"]) => {
    if (type === "approved") return <CheckIcon sx={{ fontSize: 14 }} />;
    if (type === "rejected") return <ClearIcon sx={{ fontSize: 14 }} />;
    return <SendOutlinedIcon sx={{ fontSize: 14 }} />;
  };

  const colorFor = (type: ActivityEvent["type"]) =>
    type === "approved"
      ? STATUS_ACCENT.approved
      : type === "rejected"
        ? STATUS_ACCENT.rejected
        : STATUS_ACCENT.awaiting_audit;

  const textFor = (e: ActivityEvent) =>
    e.type === "submitted"
      ? `${e.case_number} submitted to ${e.submitted_to || "review"}`
      : `${e.case_number} ${e.type}`;

  return (
    <Card elevation={0} sx={{ ...cardSx, p: 3, flex: 1, minWidth: 320 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 2 }}>
        Recent Activity
      </Typography>
      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={22} />
        </Box>
      ) : events.length === 0 ? (
        <Typography variant="body2" sx={{ color: SUBTLE, py: 2 }}>
          Activity will show up here as requisitions move through review.
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
          {events.map((e) => (
            <Stack
              key={e.id}
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "flex-start" }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  bgcolor: `${colorFor(e.type)}1A`,
                  color: colorFor(e.type),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                {iconFor(e.type)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {textFor(e)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: SUBTLE }}
                  noWrap
                  component="div"
                >
                  {e.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "#B8AC9F" }}>
                  {formatRelative(e.time)}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Case breakdown (categories + line items) — shared by the detail drawer
// ---------------------------------------------------------------------------

function CaseBreakdown({ detail }: { detail: CaseDetail }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: BORDER,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          px: 2.25,
          py: 1.25,
          bgcolor: SURFACE,
          borderBottom: "1px solid",
          borderColor: BORDER,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: SUBTLE,
          }}
        >
          DESCRIPTION
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: SUBTLE,
          }}
        >
          AMOUNT
        </Typography>
      </Stack>
      {detail.categories.map((cat, i) => {
        const hasItems = cat.line_items.length > 0;
        const computed = cat.line_items.reduce(
          (s, li) => s + Number(li.amount || 0),
          0,
        );
        const mismatched =
          hasItems && Math.abs(computed - Number(cat.amount || 0)) > 0.01;
        const isOpen = expanded.has(cat.id);
        return (
          <Box
            key={cat.id}
            sx={{
              borderBottom:
                i < detail.categories.length - 1 ? "1px solid" : "none",
              borderColor: BORDER,
            }}
          >
            <Stack
              direction="row"
              onClick={() => hasItems && toggle(cat.id)}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.25,
                py: 1.6,
                cursor: hasItems ? "pointer" : "default",
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {cat.category_type_display}
                  </Typography>
                  {mismatched && (
                    <Tooltip
                      title={`Line items total ${formatMoney(computed)}, category total is ${formatMoney(cat.amount)}`}
                    >
                      <WarningAmberIcon
                        sx={{ fontSize: 14, color: "warning.main" }}
                      />
                    </Tooltip>
                  )}
                </Stack>
                {cat.subtitle && (
                  <Typography variant="caption" sx={{ color: SUBTLE }}>
                    {cat.subtitle}
                  </Typography>
                )}
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {formatMoney(cat.amount)}
                </Typography>
                {hasItems &&
                  (isOpen ? (
                    <KeyboardArrowUpIcon sx={{ color: SUBTLE }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ color: SUBTLE }} />
                  ))}
              </Stack>
            </Stack>
            {hasItems && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2.25, pb: 1.6, pl: 3.5 }}>
                  <Stack
                    spacing={1}
                    divider={
                      <Divider flexItem sx={{ borderColor: "#EFEAE5" }} />
                    }
                  >
                    {cat.line_items.map((li) => (
                      <Stack
                        key={li.id}
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {li.label}
                          {li.source === "auto" && (
                            <Chip
                              label="auto"
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 17,
                                fontSize: 9.5,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatMoney(li.amount)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — opens for either a pending case (approve/reject) or a
// decided one (read-only decision summary)
// ---------------------------------------------------------------------------

function CaseDetailDrawer({
  caseId,
  onClose,
  onDecided,
}: {
  caseId: number | null;
  onClose: () => void;
  onDecided: () => void;
}) {
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "approve" | "reject" | null
  >(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (caseId === null) return;
    setPendingAction(null);
    setRejectReason("");
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiService.get<CaseDetail>(
          `/api/cost-analysis/cost-analyses/${caseId}/`,
        );
        if (!cancelled) setDetail(res.data);
      } catch (err) {
        console.error("Failed to load case detail:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const handleDecision = async (approved: boolean, reason?: string) => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await apiService.post(
        `/api/cost-analysis/cost-analyses/${detail.id}/${approved ? "approve" : "reject"}/`,
        approved ? {} : { reason },
      );
      setToast({
        severity: "success",
        message: approved
          ? `${detail.case_number} approved.`
          : `${detail.case_number} rejected and sent back.`,
      });
      onDecided();
      setTimeout(onClose, 600);
    } catch (err) {
      console.error(err);
      setToast({
        severity: "error",
        message: `Failed to ${approved ? "approve" : "reject"} this case. Please try again.`,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const computedTotal = detail
    ? detail.categories.reduce((s, c) => s + Number(c.amount || 0), 0)
    : 0;
  const mismatch =
    !!detail &&
    Math.abs(computedTotal - Number(detail.total_amount || 0)) > 0.01;

  return (
    <Drawer
      anchor="right"
      open={caseId !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, bgcolor: PAPER } }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2.25,
          borderBottom: "1px solid",
          borderColor: BORDER,
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>Requisition detail</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading || !detail ? (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ overflowY: "auto" }}>
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detail.case_number}
              </Typography>
              <StatusPill status={detail.status} />
            </Stack>
            <Typography variant="body2" sx={{ color: SUBTLE }}>
              {detail.title || detail.stream_title}
              {detail.client_ref ? ` — ${detail.client_ref}` : ""}
            </Typography>
            {detail.venue_name && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center", mt: 0.5 }}
              >
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: SUBTLE }} />
                <Typography variant="caption" sx={{ color: SUBTLE }}>
                  {detail.venue_name}
                </Typography>
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: "center", mt: 2 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: SUBTLE,
                }}
              >
                TOTAL REQUISITION
              </Typography>
              {mismatch && (
                <Tooltip
                  title={`Categories add up to ${formatMoney(computedTotal)}`}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 14, color: "warning.dark" }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                letterSpacing: "-0.02em",
                fontSize: 30,
              }}
            >
              {formatMoney(detail.total_amount)}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", mb: 1.5 }}
            >
              <ReceiptLongIcon sx={{ fontSize: 18, color: SUBTLE }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: SUBTLE,
                }}
              >
                LINE ITEMS
              </Typography>
            </Stack>
            <CaseBreakdown detail={detail} />
          </Box>

          {detail.notes && (
            <Box sx={{ px: 3, pb: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", mb: 1.5 }}
              >
                <NotesIcon sx={{ fontSize: 18, color: SUBTLE }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: SUBTLE,
                  }}
                >
                  NOTES
                </Typography>
              </Stack>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: BORDER,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  p: 2.25,
                }}
              >
                <Typography
                  sx={{
                    fontStyle: "italic",
                    color: "#5C534C",
                    lineHeight: 1.6,
                    fontSize: 14,
                  }}
                >
                  {detail.notes}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider />

          <Box sx={{ p: 3 }}>
            {detail.status !== "awaiting_audit" ? (
              detail.status === "rejected" && detail.rejection_reason ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: `${STATUS_ACCENT.rejected}12`,
                    border: "1px solid",
                    borderColor: `${STATUS_ACCENT.rejected}40`,
                    p: 2.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      color: STATUS_ACCENT.rejected,
                      mb: 0.5,
                    }}
                  >
                    REJECTION REASON
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4A3530" }}>
                    {detail.rejection_reason}
                  </Typography>
                </Box>
              ) : detail.status === "approved" ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <GavelOutlinedIcon
                    sx={{ fontSize: 17, color: STATUS_ACCENT.approved }}
                  />
                  <Typography variant="body2" sx={{ color: SUBTLE }}>
                    Approved on {formatDate(detail.decided_at)}
                    {detail.decided_by_name
                      ? ` by ${detail.decided_by_name}`
                      : ""}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: SUBTLE }}>
                  This case has not been submitted for review yet.
                </Typography>
              )
            ) : pendingAction === "reject" ? (
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ color: SUBTLE }}>
                  Let the submitter know what needs fixing before resubmitting.
                </Typography>
                <TextField
                  multiline
                  minRows={2}
                  placeholder="Reason for rejection (required)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  autoFocus
                  size="small"
                  sx={{ bgcolor: "#fff" }}
                />
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button
                    disabled={actionLoading}
                    onClick={() => setPendingAction(null)}
                    sx={outlinedBtnSx}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disableElevation
                    disabled={actionLoading || rejectReason.trim().length === 0}
                    onClick={() => handleDecision(false, rejectReason.trim())}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    {actionLoading && (
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1, color: "inherit" }}
                      />
                    )}
                    Confirm Rejection
                  </Button>
                </Stack>
              </Stack>
            ) : pendingAction === "approve" ? (
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ color: SUBTLE }}>
                  Approve {detail.case_number} for{" "}
                  {formatMoney(detail.total_amount)}?
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button
                    disabled={actionLoading}
                    onClick={() => setPendingAction(null)}
                    sx={outlinedBtnSx}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={actionLoading}
                    onClick={() => handleDecision(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    {actionLoading && (
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1, color: "inherit" }}
                      />
                    )}
                    Confirm Approval
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setPendingAction("reject")}
                  sx={outlinedBtnSx}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => setPendingAction("approve")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Approve Requisition
                </Button>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Main cost-analysis table (mirrors the "Partners" table)
// ---------------------------------------------------------------------------

const TAB_TO_STATUS: Record<MainTab, StatusValue | undefined> = {
  pending: "awaiting_audit",
  approved: "approved",
  rejected: "rejected",
  all: undefined,
};

function CaseTable({
  tab,
  onOpen,
  refreshKey,
}: {
  tab: MainTab;
  onOpen: (id: number) => void;
  refreshKey: number;
}) {
  const [rows, setRows] = useState<CaseListItem[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => setPage(0), [tab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          page: page + 1,
          page_size: pageSize,
        };
        const status = TAB_TO_STATUS[tab];
        if (status) params.status = status;
        const res = await apiService.get<{
          results: CaseListItem[];
          count: number;
        }>("/api/cost-analysis/cost-analyses/", params);
        if (!cancelled) {
          setRows(res.data.results);
          setCount(res.data.count);
        }
      } catch (err) {
        console.error("Failed to load cost analyses:", err);
        if (!cancelled) setError("Failed to load cases. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, page, refreshKey]);

  const visible = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.case_number.toLowerCase().includes(q) ||
        (r.title || r.stream_title).toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search by case #, stream, or title"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2, maxWidth: 340, bgcolor: "#fff" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: SUBTLE }} />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid",
          borderColor: BORDER,
          borderRadius: 2.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.1fr 1.3fr 1.1fr 0.9fr 0.9fr 0.9fr 40px",

            px: 2.5,
            py: 1.25,
            bgcolor: SURFACE,
            borderBottom: "1px solid",
            borderColor: BORDER,
          }}
        >
          {["CASE", "STREAM", "VENUE", "STATUS", "AMOUNT", "SUBMITTED", ""].map(
            (h) => (
              <Typography
                key={h}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: SUBTLE,
                }}
              >
                {h}
              </Typography>
            ),
          )}
        </Box>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={22} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: SUBTLE }}>
              No cases match this view yet.
            </Typography>
          </Box>
        ) : (
          visible.map((row, i) => (
            <Box
              key={row.id}
              onClick={() => onOpen(row.id)}
              sx={{
                display: "grid",
                gridTemplateColumns: "1.1fr 1.3fr 1.1fr 0.9fr 0.9fr 0.9fr 40px",
                px: 2.5,
                py: 1.75,
                alignItems: "center",
                cursor: "pointer",
                borderBottom: i < visible.length - 1 ? "1px solid" : "none",
                borderColor: "#F1EBE4",
                "&:hover": { bgcolor: PAPER },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                {row.case_number}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5 }} noWrap>
                {row.stream_title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: row.venue_name ? "text.primary" : SUBTLE }}
                noWrap
              >
                {row.venue_name || "—"}
              </Typography>
              <StatusPill status={row.status} />
              <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                {formatMoney(row.total_amount)}
              </Typography>
              <Typography variant="body2" sx={{ color: SUBTLE }}>
                {row.submitted_at ? formatDate(row.submitted_at) : "—"}
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 16, color: SUBTLE }} />
            </Box>
          ))
        )}
      </Box>

      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", pt: 1.5 }}
      >
        <Typography variant="caption" sx={{ color: SUBTLE }}>
          {count} case{count === 1 ? "" : "s"} · page {page + 1} of {totalPages}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            sx={{
              border: "1px solid",
              borderColor: BORDER,
              borderRadius: 2,
              bgcolor: "#fff",
              "&:hover": { borderColor: "#D8CFC7", bgcolor: "#fff" },
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            sx={{
              border: "1px solid",
              borderColor: BORDER,
              borderRadius: 2,
              bgcolor: "#fff",
              "&:hover": { borderColor: "#D8CFC7", bgcolor: "#fff" },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Top-level dashboard
// ---------------------------------------------------------------------------

export default function FinanceCostAnalysisDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [pendingCases, setPendingCases] = useState<CaseListItem[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  const [feedEvents, setFeedEvents] = useState<ActivityEvent[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);

  const [tab, setTab] = useState<MainTab>("pending");
  const [openId, setOpenId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apiService.get<Stats>(
        "/api/cost-analysis/cost-analyses/stats/",
      );
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load finance stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await apiService.get<
        { results: CaseListItem[] } | CaseListItem[]
      >("/api/cost-analysis/cost-analyses/pending/");
      setPendingCases(Array.isArray(res.data) ? res.data : res.data.results);
    } catch (err) {
      console.error("Failed to load pending requests:", err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  // Feeds Recent Activity — a small recent slice is enough; for real scale
  // this deserves its own /activity endpoint rather than reusing the list.
  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const res = await apiService.get<ActivityEvent[]>(
        "/api/cost-analysis/cost-analyses/activity/",
        { limit: 10 }, // not { params: { limit: 10 } }
      );
      setFeedEvents(res.data);
    } catch (err) {
      console.error("Failed to load activity feed:", err);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPending();
    fetchFeed();
  }, [fetchStats, fetchPending, fetchFeed]);

  const refreshAll = () => {
    fetchStats();
    fetchPending();
    fetchFeed();
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "primary.main",
          letterSpacing: "-0.02em",
          mb: 0.5,
        }}
      >
        Finance Dashboard
      </Typography>
      <Typography variant="body1" sx={{ color: SUBTLE, mb: 3 }}>
        Cost analysis requisitions across every stream — review, decide, and
        track totals.
      </Typography>

      <StatsStrip stats={stats} loading={statsLoading} />

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <PendingRequestsPanel
          cases={pendingCases}
          loading={pendingLoading}
          onOpen={setOpenId}
          onViewAll={() => setTab("pending")}
        />
        <RecentActivityPanel events={feedEvents} loading={feedLoading} />
      </Stack>

      <Card elevation={0} sx={{ ...cardSx, p: 3 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
          {(
            [
              [
                "pending",
                stats ? `Pending (${stats.pending_count})` : "Pending",
              ],
              ["approved", "Approved"],
              ["rejected", "Rejected"],
              ["all", "All Cases"],
            ] as [MainTab, string][]
          ).map(([value, label]) => (
            <Box
              key={value}
              onClick={() => setTab(value)}
              sx={{
                px: 2.25,
                py: 0.9,
                borderRadius: 2,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
                color: tab === value ? INK : SUBTLE,
                bgcolor: tab === value ? SURFACE : "transparent",
                border: "1px solid",
                borderColor: tab === value ? BORDER : "transparent",
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>

        <CaseTable tab={tab} onOpen={setOpenId} refreshKey={refreshKey} />
      </Card>

      <CaseDetailDrawer
        caseId={openId}
        onClose={() => setOpenId(null)}
        onDecided={refreshAll}
      />
    </Box>
  );
}
